import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { SectionType } from '../../types';
import {
  Layers,
  Plus,
  ChevronUp,
  ChevronDown,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  LayoutGrid,
  ListOrdered,
} from 'lucide-react';
import { capabilityService } from '../../services/capabilityService';
import { COMPONENT_REGISTRY } from './registry';

export const ComponentPalette: React.FC = () => {
  const {
    activeSite,
    activePageId,
    selectedSectionId,
    setSelectedSectionId,
    addSectionToActivePage,
    moveSectionInActivePage,
    duplicateSectionInActivePage,
    deleteSectionInActivePage,
    toggleSectionVisibility,
  } = useSite();

  const [activeTab, setActiveTab] = useState<'components' | 'structure'>('components');

  if (!activeSite) return null;

  const activePage = activeSite.pages?.find((p) => p && p.id === activePageId) || activeSite.pages?.[0];
  const pageSections = activePage?.sections || [];

  const editorCategories = capabilityService.getEditorCategories(activeSite.industry);

  return (
    <div className="w-80 bg-[#0B1324] border-r border-[#263750] flex flex-col h-full overflow-hidden select-none shrink-0 font-sans shadow-md">
      {/* Palette Tab Selector */}
      <div className="flex border-b border-[#263750] p-2 gap-1.5 bg-[#111B2E]">
        <button
          type="button"
          onClick={() => setActiveTab('components')}
          className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
            activeTab === 'components'
              ? 'bg-[#2563EB] text-white shadow-md'
              : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#162238]'
          }`}
        >
          <LayoutGrid className="w-3.5 h-3.5" />
          <span>Bileşenler</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('structure')}
          className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
            activeTab === 'structure'
              ? 'bg-[#2563EB] text-white shadow-md'
              : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#162238]'
          }`}
        >
          <ListOrdered className="w-3.5 h-3.5" />
          <span>Katmanlar ({pageSections.length})</span>
        </button>
      </div>

      {/* Tab 1: Component Catalog */}
      {activeTab === 'components' && (
        <div className="flex-1 overflow-y-auto p-3 space-y-4 custom-scrollbar">
          <div className="text-[11px] text-[#94A3B8] px-1 flex items-center justify-between">
            <span>
              {activeSite.industry === 'YACHT_RENTAL'
                ? 'Yat & Mavi Tur bileşenleri:'
                : 'Kurumsal web bölümleri:'}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 font-bold">
              {activeSite.industry === 'YACHT_RENTAL' ? 'YAT DİKEYİ' : 'KURUMSAL'}
            </span>
          </div>

          {editorCategories.map((group) => {
            const allowedItems = group.sectionTypes
              .filter((secType) => capabilityService.canUseSection(activeSite.industry, secType))
              .map((secType) => {
                const descriptor = COMPONENT_REGISTRY[secType];
                return {
                  type: secType,
                  label: descriptor?.name || secType,
                  desc: descriptor?.description || 'Bölüm bileşeni',
                };
              });

            if (allowedItems.length === 0) return null;

            return (
              <div key={group.id} className="space-y-1.5">
                <div className="text-[10px] font-extrabold uppercase tracking-wider text-[#94A3B8] px-1">
                  {group.name}
                </div>
                <div className="space-y-1.5">
                  {allowedItems.map((item) => (
                    <button
                      key={item.type}
                      type="button"
                      onClick={() => addSectionToActivePage(item.type)}
                      className="w-full text-left p-2.5 rounded-xl bg-[#162238] hover:bg-[#1D2B43] hover:border-blue-500/60 border border-[#263750] transition flex items-center justify-between group shadow-sm active:scale-[0.99]"
                    >
                      <div className="min-w-0 pr-2">
                        <div className="font-bold text-xs text-[#F8FAFC] group-hover:text-blue-300">
                          {item.label}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] mt-0.5 leading-snug line-clamp-1">
                          {item.desc}
                        </div>
                      </div>
                      <div className="w-6 h-6 rounded-lg bg-[#111B2E] group-hover:bg-[#2563EB] text-[#94A3B8] group-hover:text-white flex items-center justify-center shrink-0 transition">
                        <Plus className="w-3.5 h-3.5" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tab 2: Section Structure / Reorder */}
      {activeTab === 'structure' && (
        <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
          <div className="text-[11px] text-[#94A3B8] mb-1 px-1">
            Bölümleri yukarı/aşağı taşıyabilir, gizleyebilir veya silebilirsiniz.
          </div>

          {pageSections.map((section, idx) => {
            const isSelected = selectedSectionId === section.id;
            return (
              <div
                key={section.id}
                onClick={() => setSelectedSectionId(section.id)}
                className={`p-2.5 rounded-xl border transition cursor-pointer ${
                  isSelected
                    ? 'bg-[#2563EB]/20 border-[#2563EB] text-[#F8FAFC] shadow-sm'
                    : 'bg-[#162238] border-[#263750] text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1D2B43]'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 truncate">
                    <span className="text-[10px] font-mono text-[#94A3B8] w-4 text-center">
                      {idx + 1}
                    </span>
                    <span className="font-bold text-xs text-[#F8FAFC] truncate">
                      {section.name || section.type}
                    </span>
                    {!section.isVisible && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#111B2E] border border-[#263750] text-amber-400 font-semibold">
                        Gizli
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => moveSectionInActivePage(idx, idx - 1)}
                      title="Yukarı Taşı"
                      className="p-1 rounded-lg hover:bg-[#111B2E] text-[#94A3B8] hover:text-white disabled:opacity-30 transition"
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === pageSections.length - 1}
                      onClick={() => moveSectionInActivePage(idx, idx + 1)}
                      title="Aşağı Taşı"
                      className="p-1 rounded-lg hover:bg-[#111B2E] text-[#94A3B8] hover:text-white disabled:opacity-30 transition"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleSectionVisibility(section.id)}
                      title={section.isVisible ? 'Gizle' : 'Göster'}
                      className="p-1 rounded-lg hover:bg-[#111B2E] text-[#94A3B8] hover:text-white transition"
                    >
                      {section.isVisible ? (
                        <Eye className="w-3.5 h-3.5" />
                      ) : (
                        <EyeOff className="w-3.5 h-3.5 text-amber-400" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => duplicateSectionInActivePage(section.id)}
                      title="Çoğalt"
                      className="p-1 rounded-lg hover:bg-[#111B2E] text-[#94A3B8] hover:text-white transition"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSectionInActivePage(section.id)}
                      title="Sil"
                      className="p-1 rounded-lg hover:bg-rose-500/20 text-[#94A3B8] hover:text-[#EF4444] transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
